import React, { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { JobApplication } from "./ApplicationWizard";
import {
  jobApplicationService,
  CompanyIntelligenceResponse,
} from "../services/jobApplicationService";
import {
  intelligentSearchService,
  IntelligentSearchRequest,
  IntelligentSearchResult,
} from "../services/intelligentSearchService";
import { CompanyIntelligenceDisplay } from "./CompanyIntelligenceDisplay";
import { CompanyIntelligenceCategoryModal } from "./CompanyIntelligenceCategoryModal";
import {
  COMPANY_INTEL_CATEGORY_LABELS,
  CompanyIntelligenceCategoryKey,
} from "./companyIntelligenceCategories";
import toast from "react-hot-toast";
import "./CompanyIntelligencePanel.css";

const CATEGORY_SEARCH_POLL_INTERVAL_MS = 8000;

const isCategorySearchStillProcessing = (
  result: IntelligentSearchResult | null | undefined,
): boolean => result?.status === "processing";

const isCategorySearchPendingError = (err: unknown): boolean => {
  if (!(err instanceof Error)) {
    return false;
  }

  const message = err.message.toLowerCase();
  const status =
    typeof (err as any).status === "number" ? (err as any).status : null;

  return (
    status === 504 ||
    message.includes("endpoint request timed out") ||
    message.includes("gateway timeout") ||
    message.includes("timed out") ||
    message.includes("search is in progress") ||
    message.includes("please retry in a few seconds")
  );
};

interface CompanyIntelligencePanelProps {
  isOpen: boolean;
  application: JobApplication | null;
  onClose: () => void;
}

const CATEGORY_RECENCY_DAYS: Partial<Record<CompanyIntelligenceCategoryKey, number>> = {
  layoffs: 548,        // 18 months
  hirings: 180,        // 6 months
  scandals: 548,       // 18 months
  management: 365,     // 1 year
  company_size_growth: 365,
};

// Categories that need current news vs. evergreen review/culture content.
// 'news' → Tavily prioritises news articles (layoffs, hirings, scandals, mgmt).
// 'general' → full web search needed to reach Glassdoor, Trustpilot, etc.
const CATEGORY_TOPICS: Partial<Record<CompanyIntelligenceCategoryKey, 'news' | 'general'>> = {
  layoffs: 'news',
  hirings: 'news',
  scandals: 'news',
  management: 'news',
  company_size_growth: 'news',
  reviews_employ: 'general',
  reviews_customer: 'general',
  diversity_esg: 'general',
  culture_benefits: 'general',
  competitors_market: 'general',
};

// Generic location values that add noise without narrowing to the company.
// Only a specific city/metro is worth including in a company-targeted search.
const GENERIC_LOCATION_TERMS = new Set([
  "united states", "usa", "u.s.", "u.s.a.", "us",
  "remote", "worldwide", "anywhere", "global",
  "canada", "uk", "united kingdom", "australia",
  "europe", "india", "germany", "france",
]);

const getSpecificLocation = (location: string | null | undefined): string | null => {
  if (!location) return null;
  // Drop work-mode annotations: "(Remote)", "(Hybrid)", "(On-site)", etc.
  const cleaned = location.replace(/\s*\([^)]*\)/g, "").trim();
  if (!cleaned || GENERIC_LOCATION_TERMS.has(cleaned.toLowerCase())) return null;
  return cleaned;
};

const buildCategoryQuery = (
  categoryKey: CompanyIntelligenceCategoryKey,
  companyName: string,
  location?: string | null,
): string => {
  const rawCompany = companyName.replace(/\"/g, "").trim();

  // Detect "Entity Name (Brand/Alias)" pattern and split into two searchable phrases.
  const aliasMatch = rawCompany.match(/^(.+?)\s*\(([^)]+)\)\s*$/);
  const baseCompany = aliasMatch ? aliasMatch[1].trim() : rawCompany;
  const aliasCompany = aliasMatch ? aliasMatch[2].trim() : null;

  // Keep company name as a complete quoted phrase; use OR when there is an alias.
  // Never break into individual words — generic words like "Labs" cause false positives.
  const companyTerm = aliasCompany
    ? `("${baseCompany}" OR "${aliasCompany}")`
    : `"${baseCompany}"`;

  // Only append location when it names a specific city/region.
  const specificLocation = getSpecificLocation(location);
  const locationSuffix = specificLocation ? ` ${specificLocation}` : "";

  const prefix = baseCompany ? `${companyTerm}${locationSuffix} ` : "";

  switch (categoryKey) {
    case "layoffs":
      return `${prefix}layoffs restructuring "job cuts" "workforce reduction" downsizing severance`;
    case "hirings":
      return `${prefix}hiring "new jobs" recruiting "open roles" headcount expansion`;
    case "scandals":
      return `${prefix}scandal investigation lawsuit controversy fraud settlement "regulatory fine"`;
    case "management":
      return `${prefix}CEO executive leadership "management change" "board of directors" reorg`;
    case "reviews_employ":
      return `${prefix}employees "work-life balance" workplace culture glassdoor indeed reviews`;
    case "reviews_customer":
      return `${prefix}customers reviews complaints satisfaction trustpilot rating`;
    case "company_size_growth":
      return `${prefix}headcount "funding round" revenue growth "series" employees`;
    case "diversity_esg":
      return `${prefix}diversity inclusion DEI ESG sustainability`;
    case "culture_benefits":
      return `${prefix}culture benefits remote perks PTO "health insurance"`;
    case "competitors_market":
      return `${prefix}competitors rivals "market position" industry landscape`;
    default:
      return `${prefix}${categoryKey}`;
  }
};

export const CompanyIntelligencePanel: React.FC<
  CompanyIntelligencePanelProps
> = ({ isOpen, application, onClose }) => {
  // CRITICAL: Declare isMounted FIRST to ensure consistent hook order
  const [isMounted, setIsMounted] = useState(false);

  const [companyIntelligence, setCompanyIntelligence] =
    useState<CompanyIntelligenceResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLiveRefreshing, setIsLiveRefreshing] = useState(false);
  const [categorySearchKey, setCategorySearchKey] =
    useState<CompanyIntelligenceCategoryKey | null>(null);
  const [categorySearchQuery, setCategorySearchQuery] = useState("");
  const [categorySearchResults, setCategorySearchResults] =
    useState<IntelligentSearchResult | null>(null);
  const [categorySearchError, setCategorySearchError] = useState<string | null>(
    null,
  );
  const [isCategorySearchLoading, setIsCategorySearchLoading] = useState(false);
  const [isCategorySearchProcessing, setIsCategorySearchProcessing] =
    useState(false);
  const [isCategorySearchOpen, setIsCategorySearchOpen] = useState(false);

  // Prevent body scroll when panel is open
  useEffect(() => {
    const shouldLockBodyScroll =
      typeof window !== "undefined" && window.innerWidth <= 768;
    const previousOverflow = document.body.style.overflow;

    if (isOpen) {
      if (shouldLockBodyScroll) {
        document.body.style.overflow = "hidden";
      }
    } else {
      document.body.style.overflow = previousOverflow;
      // Reset fetch tracking when panel closes
      isFetchingRef.current = false;
      lastFetchedCompanyRef.current = null;
      setIsCategorySearchOpen(false);
      setIsCategorySearchLoading(false);
      setIsCategorySearchProcessing(false);
      setCategorySearchError(null);
      setCategorySearchKey(null);
      setCategorySearchQuery("");
      setCategorySearchResults(null);
    }
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  // Set mounted flag
  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Track if we're currently fetching to prevent concurrent fetches
  const isFetchingRef = useRef(false);
  const lastFetchedCompanyRef = useRef<string | null>(null);
  const isCategoryPollingRef = useRef(false);

  // Fetch company intelligence when panel opens
  useEffect(() => {
    if (!isOpen || !application || !application.company) {
      return;
    }

    // Prevent re-fetching if we already have valid intelligence for this company
    const companyKey = `${application.company}-${application.url || ""}-${application.location || ""}`;
    if (
      companyIntelligence?.status === "ok" &&
      lastFetchedCompanyRef.current === companyKey &&
      !isLoading
    ) {
      return;
    }

    // Prevent concurrent fetches
    if (isFetchingRef.current) {
      return;
    }

    const fetchCompanyIntelligence = async () => {
      isFetchingRef.current = true;
      setIsLoading(true);
      setError(null);

      try {
        const intelligence = await jobApplicationService.getCompanyIntelligence(
          application.company!,
          {
            url: application.url || undefined,
            city: application.location || undefined,
          },
        );

        // Handle async processing status
        if (intelligence.status === "processing") {
          toast.success(
            "Company intelligence is being processed. Please check back in about 30-60 seconds.",
            {
              duration: 4000,
              position: "top-right",
            },
          );
          setCompanyIntelligence({ status: "processing" } as any);
          setIsLoading(false);
          isFetchingRef.current = false;
          return;
        }

        setCompanyIntelligence(intelligence);
        lastFetchedCompanyRef.current = companyKey;
      } catch (err) {
        console.error("Error fetching company intelligence:", err);
        if (err instanceof Error && err.message.includes("504")) {
          toast.success(
            "Company intelligence request is being processed asynchronously. Please check back in about 30-60 seconds.",
            {
              duration: 4000,
              position: "top-right",
            },
          );
          setCompanyIntelligence({ status: "processing" } as any);
        } else {
          const errorMessage =
            err instanceof Error
              ? err.message
              : "Failed to fetch company intelligence";
          setError(errorMessage);
          toast.error(errorMessage, {
            duration: 5000,
            position: "top-right",
          });
          setCompanyIntelligence(null);
        }
      } finally {
        setIsLoading(false);
        isFetchingRef.current = false;
      }
    };

    fetchCompanyIntelligence();
  }, [isOpen, application?.company, application?.url, application?.location]);

  // Poll for results when the backend is still processing (live refresh or async run)
  useEffect(() => {
    if (!isOpen || !application || !application.company) {
      return;
    }

    if (companyIntelligence?.status !== "processing") {
      return;
    }

    const companyKey = `${application.company}-${application.url || ""}-${application.location || ""}`;

    const intervalId = window.setInterval(async () => {
      if (isFetchingRef.current) {
        return;
      }

      isFetchingRef.current = true;
      try {
        const intelligence = await jobApplicationService.getCompanyIntelligence(
          application.company!,
          {
            url: application.url || undefined,
            city: application.location || undefined,
          },
        );

        if (intelligence.status !== "processing") {
          setCompanyIntelligence(intelligence);
          lastFetchedCompanyRef.current = companyKey;
          toast.success("Company intelligence is ready", {
            duration: 2000,
            position: "top-right",
          });
        }
      } catch (err) {
        console.error("Error polling company intelligence:", err);
      } finally {
        isFetchingRef.current = false;
      }
    }, 8000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [
    companyIntelligence?.status,
    isOpen,
    application?.company,
    application?.url,
    application?.location,
  ]);

  // Handler for refreshing company intelligence
  const handleRefresh = useCallback(async () => {
    if (!application || !application.company) {
      toast.error(
        "Please enter a company name to generate company intelligence.",
        {
          duration: 4000,
          position: "top-right",
        },
      );
      return;
    }

    setIsLoading(true);
    setError(null);
    setCompanyIntelligence(null);

    try {
      const intelligence = await jobApplicationService.getCompanyIntelligence(
        application.company,
        {
          url: application.url || undefined,
          city: application.location || undefined,
        },
      );

      // Handle async processing status
      if (intelligence.status === "processing") {
        toast.success(
          "Company intelligence is being processed. Please check back in about 30-60 seconds.",
          {
            duration: 4000,
            position: "top-right",
          },
        );
        setCompanyIntelligence({ status: "processing" } as any);
        setIsLoading(false);
        return;
      }

      setCompanyIntelligence(intelligence);
      toast.success("Company intelligence refreshed", {
        duration: 2000,
        position: "top-right",
      });
    } catch (err) {
      console.error("Error fetching company intelligence:", err);
      if (err instanceof Error && err.message.includes("504")) {
        toast.success(
          "Company intelligence request is being processed asynchronously. Please check back in about 30-60 seconds.",
          {
            duration: 4000,
            position: "top-right",
          },
        );
        setCompanyIntelligence({ status: "processing" } as any);
      } else {
        const errorMessage =
          err instanceof Error
            ? err.message
            : "Failed to fetch company intelligence";
        setError(errorMessage);
        toast.error(errorMessage, {
          duration: 5000,
          position: "top-right",
        });
        setCompanyIntelligence(null);
      }
    } finally {
      setIsLoading(false);
    }
  }, [application]);

  const handleLiveRefresh = useCallback(async () => {
    if (!application || !application.company) {
      toast.error(
        "Please enter a company name to generate company intelligence.",
        {
          duration: 4000,
          position: "top-right",
        },
      );
      return;
    }

    setIsLiveRefreshing(true);
    setIsLoading(true);
    setError(null);
    setCompanyIntelligence(null);

    const companyKey = `${application.company}-${application.url || ""}-${application.location || ""}`;

    try {
      const intelligence = await jobApplicationService.getCompanyIntelligence(
        application.company,
        {
          url: application.url || undefined,
          city: application.location || undefined,
        },
        undefined,
        undefined,
        {
          forceRefresh: true,
        },
      );

      if (intelligence.status === "processing") {
        toast.success(
          "Live refresh started. This usually takes about 30-60 seconds.",
          {
            duration: 4000,
            position: "top-right",
          },
        );
        setCompanyIntelligence({ status: "processing" } as any);
        setIsLoading(false);
        setIsLiveRefreshing(false);
        return;
      }

      setCompanyIntelligence(intelligence);
      lastFetchedCompanyRef.current = companyKey;
      toast.success("Live company intelligence refreshed", {
        duration: 2000,
        position: "top-right",
      });
    } catch (err) {
      console.error("Error fetching live company intelligence:", err);
      if (err instanceof Error && err.message.includes("504")) {
        toast.success(
          "Live refresh is being processed. Please check back in about 30-60 seconds.",
          {
            duration: 4000,
            position: "top-right",
          },
        );
        setCompanyIntelligence({ status: "processing" } as any);
      } else {
        const errorMessage =
          err instanceof Error
            ? err.message
            : "Failed to refresh company intelligence";
        setError(errorMessage);
        toast.error(errorMessage, {
          duration: 5000,
          position: "top-right",
        });
        setCompanyIntelligence(null);
      }
    } finally {
      setIsLoading(false);
      setIsLiveRefreshing(false);
    }
  }, [application]);

  const buildCategorySearchRequest = useCallback(
    (
      categoryKey: CompanyIntelligenceCategoryKey,
      forceRefresh = false,
    ): IntelligentSearchRequest | null => {
      // Use the application company name only — the resolved domain (e.g.
      // "10alabs.com") is a poor search term and produces irrelevant results.
      const companyName = application?.company?.trim() || "";

      if (!companyName) {
        return null;
      }

      return {
        query: buildCategoryQuery(
          categoryKey,
          companyName,
          application?.location,
        ),
        context: companyName,
        searchType: "company",
        topic: CATEGORY_TOPICS[categoryKey],
        recencyDays: CATEGORY_RECENCY_DAYS[categoryKey],
        forceRefresh,
      };
    },
    [
      application?.company,
      application?.location,
      companyIntelligence?.resolved_company?.domain,
    ],
  );

  const runCategoryDeepDive = useCallback(
    async (
      categoryKey: CompanyIntelligenceCategoryKey,
      forceRefresh = false,
    ) => {
      if (isCategorySearchLoading) {
        return;
      }

      const request = buildCategorySearchRequest(categoryKey, forceRefresh);
      if (!request || !request.context) {
        toast.error("Company name is required to run a deep dive search.", {
          duration: 4000,
          position: "top-right",
        });
        return;
      }

      setCategorySearchKey(categoryKey);
      setCategorySearchQuery(request.query);
      setCategorySearchResults(null);
      setCategorySearchError(null);
      setIsCategorySearchOpen(true);
      setIsCategorySearchLoading(true);
      setIsCategorySearchProcessing(false);

      let shouldPollForResult = false;

      try {
        const result = await intelligentSearchService.search(request);
        if (isCategorySearchStillProcessing(result)) {
          shouldPollForResult = true;
          toast.success(
            "Deep dive is being processed. Results will appear automatically in about 30-60 seconds.",
            {
              duration: 4000,
              position: "top-right",
            },
          );
          return;
        }

        setCategorySearchResults(result);
      } catch (err) {
        console.error("Error running category deep dive:", err);
        if (isCategorySearchPendingError(err)) {
          shouldPollForResult = true;
          setCategorySearchError(null);
          toast.success(
            "Deep dive is still processing. Results will appear automatically in about 30-60 seconds.",
            {
              duration: 4000,
              position: "top-right",
            },
          );
        } else {
          const errorMessage =
            err instanceof Error
              ? err.message
              : "Failed to run expanded search";
          setCategorySearchError(errorMessage);
          toast.error(errorMessage, {
            duration: 5000,
            position: "top-right",
          });
        }
      } finally {
        setIsCategorySearchProcessing(shouldPollForResult);
        if (!shouldPollForResult) {
          setIsCategorySearchLoading(false);
        }
      }
    },
    [buildCategorySearchRequest, isCategorySearchLoading],
  );

  const handleCategoryDeepDive = useCallback(
    (categoryKey: CompanyIntelligenceCategoryKey) => {
      runCategoryDeepDive(categoryKey);
    },
    [runCategoryDeepDive],
  );

  const handleCategoryDeepDiveRefresh = useCallback(() => {
    if (!categorySearchKey) {
      return;
    }
    runCategoryDeepDive(categorySearchKey, true);
  }, [categorySearchKey, runCategoryDeepDive]);

  const handleCategorySearchClose = useCallback(() => {
    setIsCategorySearchOpen(false);
    setIsCategorySearchLoading(false);
    setIsCategorySearchProcessing(false);
  }, []);

  useEffect(() => {
    if (
      !isOpen ||
      !isCategorySearchOpen ||
      !isCategorySearchProcessing ||
      !categorySearchKey
    ) {
      return;
    }

    const request = buildCategorySearchRequest(categorySearchKey);
    if (!request) {
      setIsCategorySearchProcessing(false);
      setIsCategorySearchLoading(false);
      return;
    }

    const intervalId = window.setInterval(async () => {
      if (isCategoryPollingRef.current) {
        return;
      }

      isCategoryPollingRef.current = true;
      try {
        const result = await intelligentSearchService.search(request);
        if (isCategorySearchStillProcessing(result)) {
          return;
        }

        setCategorySearchResults(result);
        setCategorySearchError(null);
        setIsCategorySearchProcessing(false);
        setIsCategorySearchLoading(false);
        toast.success("Deep dive is ready", {
          duration: 2000,
          position: "top-right",
        });
      } catch (err) {
        if (isCategorySearchPendingError(err)) {
          return;
        }

        console.error("Error polling category deep dive:", err);
        const errorMessage =
          err instanceof Error ? err.message : "Failed to run expanded search";
        setCategorySearchError(errorMessage);
        setIsCategorySearchProcessing(false);
        setIsCategorySearchLoading(false);
        toast.error(errorMessage, {
          duration: 5000,
          position: "top-right",
        });
      } finally {
        isCategoryPollingRef.current = false;
      }
    }, CATEGORY_SEARCH_POLL_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
      isCategoryPollingRef.current = false;
    };
  }, [
    buildCategorySearchRequest,
    categorySearchKey,
    isCategorySearchOpen,
    isCategorySearchProcessing,
    isOpen,
  ]);

  // CRITICAL: Check isOpen AFTER all hooks
  if (!isOpen) {
    return null;
  }

  // Early return for mounting check (client-side only)
  if (!isMounted || typeof document === "undefined" || !document.body) {
    return null;
  }

  const isLiveRefreshProcessing = companyIntelligence?.status === "processing";
  const isLiveRefreshDisabled =
    isLoading || isLiveRefreshing || isLiveRefreshProcessing;
  const canDeepDive = true;
  const activeCategoryLabel = categorySearchKey
    ? COMPANY_INTEL_CATEGORY_LABELS[categorySearchKey]
    : "Category";
  const deepDiveCompanyName = application?.company || "";

  const panelContent = (
    <div
      className="company-intelligence-panel-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className="company-intelligence-panel"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="company-intelligence-panel-header">
          <div className="company-intelligence-panel-title">
            <h2>🏢 Company Intelligence</h2>
            {application?.company && (
              <p className="company-intelligence-panel-subtitle">
                {application.company}
                {application.position && ` - ${application.position}`}
              </p>
            )}
          </div>
          <div className="company-intelligence-panel-header-actions">
            {application?.company && (
              <button
                className="company-intelligence-panel-refresh-btn"
                onClick={handleRefresh}
                disabled={isLoading || isLiveRefreshing}
                title="Refresh Company Intelligence"
              >
                {isLoading ? "⏳" : "🔄"}
              </button>
            )}
            {application?.company && (
              <button
                className="company-intelligence-panel-live-refresh-btn"
                onClick={handleLiveRefresh}
                disabled={isLiveRefreshDisabled}
                title="Live refresh (costs tokens): bypasses cache and pulls the latest company intelligence."
                data-tooltip="Live refresh (costs tokens): bypasses cache and pulls the latest company intelligence."
                aria-label="Live refresh (costs tokens)"
              >
                <span
                  className={`live-refresh-icon ${isLiveRefreshing ? "is-animating" : ""}`}
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <defs>
                      <linearGradient
                        id="liveRefreshCoin"
                        x1="0"
                        y1="0"
                        x2="1"
                        y2="1"
                      >
                        <stop offset="0%" stopColor="#fff2b2" />
                        <stop offset="45%" stopColor="#fbbf24" />
                        <stop offset="100%" stopColor="#b45309" />
                      </linearGradient>
                    </defs>
                    <circle
                      cx="12"
                      cy="12"
                      r="8.2"
                      fill="url(#liveRefreshCoin)"
                      stroke="rgba(15, 23, 42, 0.25)"
                      strokeWidth="1"
                    />
                    <path
                      d="M12 6.5l-3.1 5.6h3.2l-1.2 5.4 4.6-6.4h-3.2z"
                      fill="#0f172a"
                    />
                    <circle cx="18.2" cy="7.2" r="1.1" fill="#fef3c7" />
                  </svg>
                </span>
              </button>
            )}
            <button
              className="company-intelligence-panel-close"
              onClick={onClose}
              aria-label="Close"
            >
              ✕
            </button>
          </div>
        </div>

        <div className="company-intelligence-panel-content">
          <CompanyIntelligenceDisplay
            intelligence={companyIntelligence}
            isLoading={isLoading}
            error={error}
            hideOverallScore={false}
            onCategoryDeepDive={handleCategoryDeepDive}
            deepDiveActiveCategory={categorySearchKey}
            deepDiveLoading={isCategorySearchLoading}
            deepDiveDisabled={!canDeepDive}
            deepDiveDisabledMessage="Intelligent Search is not available in your current plan."
          />
        </div>
      </div>
      <CompanyIntelligenceCategoryModal
        isOpen={isCategorySearchOpen}
        onClose={handleCategorySearchClose}
        categoryLabel={activeCategoryLabel}
        companyName={deepDiveCompanyName}
        query={categorySearchQuery}
        results={categorySearchResults}
        isLoading={isCategorySearchLoading}
        error={categorySearchError}
        onRefresh={handleCategoryDeepDiveRefresh}
      />
    </div>
  );

  return createPortal(panelContent, document.body);
};
